import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@stenpro/localization";
import classNames from "../components/classNames";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { useIsMobile } from "../hooks/useMediaQuery";
import { appRegistry, type AppRegistryEntry } from "./appRegistry";

type AppSwitcherButtonProps = {
  isOpen: boolean;
  onToggle: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
  className?: string;
  variant: AppSwitcherVariant;
};

function AppSwitcherButton({ isOpen, onToggle, buttonRef, className, variant }: AppSwitcherButtonProps) {
  const { t } = useTranslation();
  const label = t("appSwitcher.ariaLabel");
  const toneClassName =
    variant === "topbar"
      ? "h-9 w-9 rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:ring-ring focus-visible:ring-offset-background"
      : "h-10 w-10 rounded-lg text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:ring-ring focus-visible:ring-offset-background";

  const button = (
    <button
      ref={buttonRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-label={label}
      onClick={onToggle}
      title={label}
      className={classNames(
        "inline-flex items-center justify-center p-0 shadow-none transition-colors duration-fast ease-out active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
        toneClassName,
        className
      )}
    >
      <span className="sr-only">{isOpen ? t("appSwitcher.close") : t("appSwitcher.open")}</span>
      <svg viewBox="0 0 24 24" className={variant === "topbar" ? "h-5 w-5" : "h-6 w-6"} width="24" height="24" aria-hidden="true">
        <circle cx="6" cy="6" r="2" fill="currentColor" />
        <circle cx="12" cy="6" r="2" fill="currentColor" />
        <circle cx="18" cy="6" r="2" fill="currentColor" />
        <circle cx="6" cy="12" r="2" fill="currentColor" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
        <circle cx="18" cy="12" r="2" fill="currentColor" />
        <circle cx="6" cy="18" r="2" fill="currentColor" />
        <circle cx="12" cy="18" r="2" fill="currentColor" />
        <circle cx="18" cy="18" r="2" fill="currentColor" />
      </svg>
    </button>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type AppSwitcherPanelProps = {
  apps: AppRegistryEntry[];
  currentAppId?: string;
  onSelectApp: (app: AppRegistryEntry) => void;
  onClose: (options?: { restoreFocus?: boolean }) => void;
  panelRef?: React.Ref<HTMLDivElement>;
  panelStyle?: CSSProperties;
  embedded?: boolean;
};

function AppSwitcherPanel({
  apps,
  currentAppId,
  onSelectApp,
  onClose,
  panelRef,
  panelStyle,
  embedded = false,
}: AppSwitcherPanelProps) {
  const { t } = useTranslation();
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [columns, setColumns] = useState(3);
  const [useFallbackStyles, setUseFallbackStyles] = useState(false);

  useEffect(() => {
    const firstIndex = apps.findIndex((app) => !app.disabled);
    if (firstIndex >= 0) {
      itemRefs.current[firstIndex]?.focus();
    }
  }, [apps]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 1024px)");
    const tablet = window.matchMedia("(min-width: 640px)");

    const updateColumns = () => {
      if (desktop.matches) {
        setColumns(3);
      } else if (tablet.matches) {
        setColumns(2);
      } else {
        setColumns(1);
      }
    };

    updateColumns();
    desktop.addEventListener("change", updateColumns);
    tablet.addEventListener("change", updateColumns);
    return () => {
      desktop.removeEventListener("change", updateColumns);
      tablet.removeEventListener("change", updateColumns);
    };
  }, []);

  useLayoutEffect(() => {
    if (embedded) return;
    const panel = panelRef && "current" in panelRef ? panelRef.current : null;
    if (!panel || typeof window === "undefined") return;
    const computed = window.getComputedStyle(panel);
    setUseFallbackStyles(computed.position !== "absolute" && computed.position !== "fixed");
  }, [embedded, panelRef]);

  const focusIndex = (index: number, step: number) => {
    let nextIndex = index;
    while (nextIndex >= 0 && nextIndex < apps.length) {
      if (!apps[nextIndex]?.disabled) {
        itemRefs.current[nextIndex]?.focus();
        return;
      }
      nextIndex += step;
    }
  };

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label={t("appSwitcher.ariaLabel")}
      style={
        embedded
          ? undefined
          : 
        useFallbackStyles
          ? {
              position: panelStyle?.position ?? "absolute",
              top: panelStyle?.top,
              left: panelStyle?.left,
              right: panelStyle ? undefined : 0,
              marginTop: panelStyle ? undefined : 12,
              width: panelStyle?.width ?? 320,
              maxHeight: panelStyle?.maxHeight ?? "80vh",
              overflow: "auto",
              borderRadius: 12,
              padding: 16,
              border: "1px solid oklch(var(--border))",
              background: "oklch(var(--popover))",
              color: "oklch(var(--popover-foreground))",
              boxShadow: "var(--shadow-lg)",
              zIndex: PANEL_Z_INDEX,
            }
          : panelStyle
      }
      className={
        embedded
          ? "w-full overflow-auto text-popover-foreground"
          : panelStyle
          ? "fixed z-[1000] w-[320px] max-h-[80vh] overflow-auto rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg"
          : "absolute right-0 z-[1000] mt-3 w-[320px] max-h-[80vh] overflow-auto rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg"
      }
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose({ restoreFocus: true });
          return;
        }
        const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const currentIndex = itemRefs.current.findIndex(
          (item) => item === document.activeElement
        );
        if (event.key === "Home") {
          focusIndex(0, 1);
          return;
        }
        if (event.key === "End") {
          focusIndex(apps.length - 1, -1);
          return;
        }
        if (currentIndex === -1) {
          focusIndex(0, 1);
          return;
        }
        const offset =
          event.key === "ArrowRight"
            ? 1
            : event.key === "ArrowLeft"
              ? -1
              : event.key === "ArrowDown"
                ? columns
                : -columns;
        focusIndex(currentIndex + offset, offset >= 0 ? 1 : -1);
      }}
    >
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: embedded ? "repeat(3, minmax(0, 1fr))" : `repeat(${columns}, 88px)`,
          ...(useFallbackStyles ? { display: "grid", gap: 12 } : {}),
        }}
      >
        {apps.map((app, index) => {
          const isActive = app.id === currentAppId;
          const isDisabled = Boolean(app.disabled);
          const label = t(app.titleKey, app.title);

          return (
            <button
              key={app.id}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitem"
              aria-current={isActive ? "true" : undefined}
              aria-disabled={isDisabled}
              disabled={isDisabled}
              title={isDisabled ? t("appSwitcher.disabled") : undefined}
              onClick={() => {
                if (isDisabled || isActive) return;
                onSelectApp(app);
              }}
              style={{
                width: embedded ? "100%" : 88,
                height: 88,
                borderRadius: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                textAlign: "center",
                padding: 6,
              }}
              className={classNames(
                "group flex h-[88px] w-[88px] flex-col items-center justify-center gap-2 rounded-[10px] border border-transparent bg-popover text-center text-popover-foreground outline-none transition-colors duration-fast ease-out hover:border-border hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                isActive && "border-module-accent bg-[oklch(var(--module-accent)/0.1)] text-[oklch(var(--module-accent))]",
                isDisabled && "cursor-not-allowed opacity-40 pointer-events-none"
              )}
            >
              <span
                className="flex h-8 w-8 items-center justify-center text-current"
                style={{
                  width: 32,
                  height: 32,
                }}
              >
                {app.icon}
              </span>
              <span
                className={classNames(
                  "line-clamp-2 text-[12px] leading-tight text-current",
                  isActive ? "font-semibold" : "font-medium"
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export type AppSwitcherProps = {
  apps?: AppRegistryEntry[];
  currentAppId?: string;
  onNavigate?: (app: AppRegistryEntry) => void;
  buttonClassName?: string;
  placement?: AppSwitcherPlacement;
  variant?: AppSwitcherVariant;
};

export type AppSwitcherPlacement = "fixed-top-right" | "inline";
export type AppSwitcherVariant = "standalone" | "topbar";

const FIXED_TOP_RIGHT_STYLE: CSSProperties = {
  position: "fixed",
  top: 16,
  right: 16,
  zIndex: 70,
};

const PANEL_WIDTH = 320;
const PANEL_MARGIN = 8;
const PANEL_OFFSET = 12;
const PANEL_Z_INDEX = 1000;

export function AppSwitcher({
  apps,
  currentAppId,
  onNavigate,
  buttonClassName,
  placement = "fixed-top-right",
  variant = "standalone",
}: AppSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inlinePanelStyle, setInlinePanelStyle] = useState<CSSProperties>();
  const isMobile = useIsMobile();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const entries = useMemo(() => apps ?? appRegistry, [apps]);
  const resolvedCurrentAppId = useMemo(
    () => currentAppId ?? inferCurrentAppId(entries),
    [currentAppId, entries],
  );
  const containerClassName =
    placement === "fixed-top-right" ? "fixed top-4 right-4 z-[70] inline-flex" : "relative inline-flex";
  const containerStyle = placement === "fixed-top-right" ? FIXED_TOP_RIGHT_STYLE : undefined;
  const closePanel = (options?: { restoreFocus?: boolean }) => {
    setIsOpen(false);
    setInlinePanelStyle(undefined);
    if (options?.restoreFocus) {
      buttonRef.current?.focus();
    }
  };

  const updateInlinePanelPosition = useCallback(() => {
    if (placement !== "inline" || typeof window === "undefined") {
      setInlinePanelStyle(undefined);
      return;
    }

    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const availableWidth = Math.max(0, window.innerWidth - PANEL_MARGIN * 2);
    const width = Math.min(PANEL_WIDTH, availableWidth);
    const maxLeft = Math.max(PANEL_MARGIN, window.innerWidth - width - PANEL_MARGIN);
    const left = Math.min(Math.max(rect.right - width, PANEL_MARGIN), maxLeft);
    const top = rect.bottom + PANEL_OFFSET;
    const maxHeight = Math.max(160, window.innerHeight - top - PANEL_MARGIN);

    setInlinePanelStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
      zIndex: PANEL_Z_INDEX,
    });
  }, [placement]);

  useLayoutEffect(() => {
    if (!isOpen || placement !== "inline") return;

    updateInlinePanelPosition();
    window.addEventListener("resize", updateInlinePanelPosition);
    window.addEventListener("scroll", updateInlinePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updateInlinePanelPosition);
      window.removeEventListener("scroll", updateInlinePanelPosition, true);
    };
  }, [isOpen, placement, updateInlinePanelPosition]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "a") {
        event.preventDefault();
        setIsOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      closePanel();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePanel({ restoreFocus: true });
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isMobile, isOpen]);

  const handleSelect = (app: AppRegistryEntry) => {
    if (onNavigate) {
      onNavigate(app);
    } else if (app.href) {
      window.location.assign(buildAppSwitchLaunchHref(app));
    }
    closePanel({ restoreFocus: true });
  };

  const panel =
    isOpen ? (
      <AppSwitcherPanel
        apps={entries}
        currentAppId={resolvedCurrentAppId}
        onSelectApp={handleSelect}
        onClose={closePanel}
        panelRef={panelRef}
        panelStyle={!isMobile && placement === "inline" ? inlinePanelStyle : undefined}
        embedded={isMobile}
      />
    ) : null;
  const shouldPortalInlinePanel =
    !isMobile && placement === "inline" && panel && typeof document !== "undefined";

  return (
    <div className={containerClassName} style={containerStyle}>
      <AppSwitcherButton
        isOpen={isOpen}
        onToggle={() => setIsOpen((prev) => !prev)}
        buttonRef={buttonRef}
        className={buttonClassName}
        variant={variant}
      />
      {isMobile ? (
        <Sheet open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : closePanel({ restoreFocus: true }))}>
          <SheetContent side="bottom" className="max-h-[85dvh] overflow-auto p-4 pb-[calc(var(--safe-bottom)+1rem)]">
            {panel}
          </SheetContent>
        </Sheet>
      ) : shouldPortalInlinePanel ? (
        createPortal(panel, document.body)
      ) : (
        panel
      )}
    </div>
  );
}

const appendSsoLaunchParams = (url: URL, redirectPath = "/") => {
  url.searchParams.set("sso", "1");
  url.searchParams.set("redirect", redirectPath);
  return url.toString();
};

function inferCurrentAppId(apps: AppRegistryEntry[]) {
  if (typeof window === "undefined") return undefined;
  const current = new URL(window.location.href);
  const exact = apps.find((app) => {
    try {
      const target = new URL(app.baseUrl || app.href, window.location.href);
      return target.origin === current.origin;
    } catch {
      return false;
    }
  });
  return exact?.id;
}

export const buildAppSwitchLaunchHref = (app: AppRegistryEntry) => {
  if (typeof window === "undefined") {
    return app.href;
  }

  try {
    const target = new URL(app.href, window.location.href);
    if (target.origin === window.location.origin) {
      return app.href;
    }

    if (target.pathname.startsWith("/open/")) {
      return appendSsoLaunchParams(target);
    }

    if (app.id === "portal" || app.id === "docs") {
      return appendSsoLaunchParams(target);
    }

    if (["board", "project", "fastighet", "sustain"].includes(app.id)) {
      target.pathname = "/login";
      target.search = "";
      target.hash = "";
      return appendSsoLaunchParams(target);
    }

    return app.href;
  } catch {
    return app.href;
  }
};
